import { useCallback, useState } from 'react';
import { authFetchWithRecovery } from '../../utils/sessionRecovery';

/** A refused or failed admin write, with the server's message and code. */
export class AdminRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
    public readonly details: Record<string, unknown> | null,
  ) {
    super(message);
    this.name = 'AdminRequestError';
  }
}

/** Thrown when the session ended and the browser is already going to sign-in. */
export class SessionEndedError extends Error {
  constructor() {
    super('Your session ended. Signing you in again…');
    this.name = 'SessionEndedError';
  }
}

/**
 * One `/api/admin` write, through the same session recovery the directory
 * reads use. Resolves to the JSON body; rejects with AdminRequestError.
 */
export async function adminRequest<T = Record<string, unknown>>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  let response: Response | null;
  try {
    ({ response } = await authFetchWithRecovery(`/api/admin${path}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }));
  } catch {
    throw new AdminRequestError('Could not reach Central. Check your connection and try again.', 0, null, null);
  }
  if (response === null) throw new SessionEndedError();
  const json = (await response.json().catch(() => null)) as
    | (T & { ok?: boolean; error?: string; code?: string; details?: Record<string, unknown> })
    | null;
  if (!response.ok || !json) {
    const message =
      response.status === 403 && json?.code === 'CENTRAL_ADMIN_REQUIRED'
        ? 'You need Central admin access to do this.'
        : json?.error || `Request failed (${response.status})`;
    throw new AdminRequestError(message, response.status, json?.code ?? null, json?.details ?? null);
  }
  return json;
}

export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}

/** Busy + error state for one dialog's request. */
export function useAdminAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      if (!(err instanceof SessionEndedError)) setError(errorMessage(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  const reset = useCallback(() => setError(null), []);
  return { busy, error, run, reset, setError };
}
